import {
  Category,
  GetRecipeQuery_recipe_Recipe,
  RecipeCategory,
  RecipeCreateInput,
  RecipeTag,
  RecipeUpdateInput,
  Tag,
} from '__generated__/queries-recipe';
import { formatISO } from 'date-fns';
import { paths, replaceParams } from 'routing';

export const NEW_RECIPE_CATEGORY_ID = 'NEW_RECIPE_CATEGORY_ID';
export const NEW_RECIPE_TAG_ID = 'NEW_RECIPE_TAG_ID';
export const NEW_TAG_ID = 'NEW_TAG_ID';

interface RecipeCategoryDefined extends RecipeCategory {
  category: Category;
}

interface RecipeTagDefined extends RecipeTag {
  tag: Tag;
}

export const getRecipeUpdateInput = (
  recipe: GetRecipeQuery_recipe_Recipe,
): RecipeUpdateInput => {
  const unmutableKeys: (keyof GetRecipeQuery_recipe_Recipe)[] = [
    'id',
    '__typename',
    'brandKey',
    'status',
    'lockedBy',
    'lockedAt',
    'createdAt',
    'authorName',
    'editedAt',
  ];

  const recipeWithoutUnmutableKeys = Object.entries(recipe).reduce(
    (prev, [key, value]) =>
      unmutableKeys.includes(key as keyof GetRecipeQuery_recipe_Recipe)
        ? prev
        : { ...prev, [key]: value },
    {},
  ) as Omit<
    GetRecipeQuery_recipe_Recipe,
    | 'id'
    | '__typename'
    | 'brandKey'
    | 'status'
    | 'lockedBy'
    | 'lockedAt'
    | 'createdAt'
    | 'authorName'
    | 'editedAt'
  >;

  const recipeCategoriesToCreate = recipe.recipeCategories.filter(
    ({ id, category }) => id === NEW_RECIPE_CATEGORY_ID && category,
  ) as RecipeCategoryDefined[];
  const recipeCategoriesToDelete = recipe.recipeCategories.filter(
    ({ category }) => !category,
  );
  const recipeCategoriesToUpdate = recipe.recipeCategories.filter(
    ({ id, category }) => id !== NEW_RECIPE_CATEGORY_ID && category,
  ) as RecipeCategoryDefined[];

  const recipeTagsToCreate = recipe.recipeTags.filter(
    ({ id, tag }) => id.startsWith(NEW_RECIPE_TAG_ID) && tag,
  ) as RecipeTagDefined[];
  const recipeTagsToDelete = recipe.recipeTags.filter(
    ({ id, tag }) => !id.startsWith(NEW_RECIPE_TAG_ID) && !tag,
  );

  return {
    ...recipeWithoutUnmutableKeys,
    recipeCategories: {
      create: recipeCategoriesToCreate.map(({ position, category }) => ({
        position,
        category: { connect: { id: category.id } },
      })),
      delete: recipeCategoriesToDelete.map(({ id }) => ({ id })),
      update: recipeCategoriesToUpdate.map(({ id, category }) => ({
        where: { id },
        data: {
          category: { connect: { id: category.id } },
        },
      })),
    },
    recipeTags: {
      create: recipeTagsToCreate.map(({ tag }) => ({
        tag:
          tag.id === NEW_TAG_ID
            ? { create: { title: tag.title } }
            : { connect: { id: tag.id } },
      })),
      delete: recipeTagsToDelete.map(({ id }) => ({ id })),
    },
    editedAt: formatISO(new Date()),
    recipeUrls: undefined,
    recipeHistories: undefined,
  };
};

export const getRecipeCreateInput = (
  recipe: GetRecipeQuery_recipe_Recipe,
): RecipeCreateInput => {
  const unmutableKeys: (keyof GetRecipeQuery_recipe_Recipe)[] = [
    'id',
    '__typename',
    'lockedBy',
    'lockedAt',
    'createdAt',
    'authorName',
    'editedAt',
  ];

  const recipeWithoutUnmutableKeys = Object.entries(recipe).reduce(
    (prev, [key, value]) =>
      unmutableKeys.includes(key as keyof GetRecipeQuery_recipe_Recipe)
        ? prev
        : { ...prev, [key]: value },
    {},
  ) as Omit<
    GetRecipeQuery_recipe_Recipe,
    | 'id'
    | '__typename'
    | 'lockedBy'
    | 'lockedAt'
    | 'createdAt'
    | 'authorName'
    | 'editedAt'
  >;

  const recipeCategoriesToCreate = recipe.recipeCategories.filter(
    ({ id, category }) => id === NEW_RECIPE_CATEGORY_ID && category,
  ) as RecipeCategoryDefined[];

  const recipeTagsToCreate = recipe.recipeTags.filter(
    ({ id, tag }) => id.startsWith(NEW_RECIPE_TAG_ID) && tag,
  ) as RecipeTagDefined[];

  return {
    ...recipeWithoutUnmutableKeys,
    recipeCategories: {
      create: recipeCategoriesToCreate.map(({ position, category }) => ({
        position,
        category: { connect: { id: category.id } },
      })),
    },
    recipeTags: {
      create: recipeTagsToCreate.map(({ tag }) => ({
        tag:
          tag.id === NEW_TAG_ID
            ? { create: { title: tag.title } }
            : { connect: { id: tag.id } },
      })),
    },
    editedAt: formatISO(new Date()),
    recipeUrls: undefined,
    recipeHistories: undefined,
  };
};

export const getUnlockLink = (id: string) =>
  `${window.location.origin}${replaceParams(paths.RECIPE_UNLOCK, {
    id,
  })}`;
